import { useEffect, useState } from 'react';
import { API_KEY_SCOPES, EVENTS } from '@orion/contracts';
import type { ApiKeyResponse, ApiKeyScope, WebhookSubscriptionResponse } from '@orion/contracts';
import { Button, ConfirmDialog, Dialog, Input, Select } from '@orion/ui';
import { PageHeader } from '../../../app/shell.js';
import { ApiError } from '../../../lib/api-client.js';
import { useApiKeysQuery, useCreateApiKey, useRevokeApiKey } from '../api-keys/api.js';
import {
  useCreateWebhookSubscription,
  useDeleteWebhookSubscription,
  useRedeliverWebhookDelivery,
  useRegenerateWebhookSecret,
  useUpdateWebhookSubscription,
  useWebhookDeliveriesQuery,
  useWebhookSubscriptionsQuery,
} from './api.js';

const ALL_EVENT_TYPES = Object.values(EVENTS);
const SCOPE_LABEL: Record<ApiKeyScope, string> = {
  'leads:write': 'leads:write — create/upsert leads',
  'leads:read': 'leads:read — reserved, not yet backed by an endpoint',
  'contacts:write': 'contacts:write — create/upsert contacts',
  'activities:write': 'activities:write — log activity against a lead/contact',
  'tasks:write': 'tasks:write — create follow-up tasks',
};
const SCOPE_OPTIONS: Array<{ value: ApiKeyScope; label: string }> = API_KEY_SCOPES.map((value) => ({ value, label: SCOPE_LABEL[value] }));
const EXPIRY_OPTIONS = [
  { value: '', label: 'Never expires' },
  { value: '90', label: 'In 90 days' },
  { value: '180', label: 'In 180 days' },
  { value: '365', label: 'In 1 year' },
];
// The relay's own constants (WebhookRelayService) — shown for real instead
// of guessing a round number for the design's "attempt 8/8" copy.
const MAX_ATTEMPTS_PER_DELIVERY = 6;
const CONSECUTIVE_FAILURES_TO_DISABLE = 5;

// design/orion-crm-design/rendered-html/AdminWebhooks.html freezes with the
// failing endpoint selected (not just the list's first row).
const isDesignWebhooksPreview = import.meta.env.DEV && new URLSearchParams(window.location.search).get('__design') === 'AdminWebhooks';

const DELIVERY_STATUS_COLOR: Record<string, { bg: string; fg: string }> = {
  pending: { bg: 'var(--color-amber-soft)', fg: 'var(--color-amber-text)' },
  delivered: { bg: 'var(--color-success-soft)', fg: 'var(--color-success)' },
  failed: { bg: 'var(--color-danger-soft)', fg: 'var(--color-danger)' },
};

function initials(name: string): string {
  return name.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();
}

function CreateApiKeyDialog({ onClose }: { onClose: () => void }) {
  const create = useCreateApiKey();
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<Set<string>>(new Set(['leads:write']));
  const [expiry, setExpiry] = useState('');
  const [secret, setSecret] = useState<string | null>(null);
  const error = create.error instanceof ApiError ? (create.error.problem.detail ?? create.error.problem.title) : undefined;

  const toggleScope = (s: string) =>
    setScopes((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });

  if (secret) {
    return (
      <Dialog open title="Copy this key now" onClose={onClose}>
        <p className="mb-2 text-sm font-semibold text-amber-text">It won&rsquo;t be shown again.</p>
        <code className="block break-all rounded-control bg-surface-subtle px-3 py-2 text-xs">{secret}</code>
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      title="Create API key"
      onClose={onClose}
      actions={
        <Button
          size="sm"
          loading={create.isPending}
          onClick={async () => {
            if (!name.trim() || scopes.size === 0) return;
            const result = await create.mutateAsync({
              name: name.trim(),
              scopes: [...scopes] as ApiKeyScope[],
              expiresAt: expiry ? new Date(Date.now() + Number(expiry) * 86_400_000).toISOString() : undefined,
            });
            setSecret(result.secret);
          }}
        >
          Create key
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <Input label="Key name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Website form ingest" />
        <div className="flex flex-col gap-1.5">
          <span className="text-[13px] font-semibold text-ink">Scopes</span>
          {SCOPE_OPTIONS.map((s) => (
            <label key={s.value} className="flex items-center gap-2 text-sm text-text-secondary">
              <input type="checkbox" checked={scopes.has(s.value)} onChange={() => toggleScope(s.value)} />
              {s.label}
            </label>
          ))}
        </div>
        <Select label="Expires" value={expiry} onChange={(e) => setExpiry(e.target.value)} options={EXPIRY_OPTIONS} />
        {error && (
          <div role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-sm font-medium text-danger">
            {error}
          </div>
        )}
      </div>
    </Dialog>
  );
}

function WebhookDetail({ subscription }: { subscription: WebhookSubscriptionResponse }) {
  const update = useUpdateWebhookSubscription();
  const regenerate = useRegenerateWebhookSecret();
  const redeliver = useRedeliverWebhookDelivery(subscription.id);
  const { data: deliveries } = useWebhookDeliveriesQuery(subscription.id);
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);

  const toggleEvent = (eventType: string) => {
    const current = new Set(subscription.eventTypes);
    if (current.has(eventType)) current.delete(eventType);
    else current.add(eventType);
    update.mutate({ id: subscription.id, input: { eventTypes: [...current] } });
  };

  return (
    <div className="grid grid-cols-[272px_1fr] bg-surface-subtle">
      <div className="flex flex-col gap-3 border-r border-divider p-5">
        <div className="flex flex-col gap-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">Signing secret</span>
          <div className="flex gap-1.5">
            <span className="flex h-8 flex-1 items-center rounded-control border border-border bg-surface px-2.5 font-mono text-xs text-text-secondary">
              {revealedSecret ?? 'whsec_••••••••••••••••'}
            </span>
            <button
              type="button"
              disabled
              title="Secret is only ever shown once, right after it's created or rotated"
              className="flex h-8 items-center rounded-chip bg-surface-subtle px-2.5 text-xs font-semibold text-text-muted"
            >
              Reveal once
            </button>
          </div>
          <span className="text-xs text-text-muted">
            <button
              type="button"
              onClick={async () => {
                const result = await regenerate.mutateAsync(subscription.id);
                setRevealedSecret(result.secret);
              }}
              className="font-semibold text-primary"
            >
              Rotate secret
            </button>
          </span>
        </div>
        <div className="rounded-control border border-divider bg-surface p-2.5 text-xs leading-4 text-text-secondary">
          Signatures: HMAC-SHA256 in <span className="font-mono text-[11px] text-ink">X-Orion-Signature</span>
        </div>
        <fieldset className="flex flex-col gap-1.5">
          <legend className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-muted">Events</legend>
          <div className="grid grid-cols-2 gap-x-2 gap-y-1.5 font-mono text-[11px] text-text-secondary">
            {ALL_EVENT_TYPES.map((eventType) => (
              <label key={eventType} className="flex items-center gap-1.5">
                <input type="checkbox" checked={subscription.eventTypes.includes(eventType)} onChange={() => toggleEvent(eventType)} />
                {eventType}
              </label>
            ))}
          </div>
        </fieldset>
      </div>

      <div className="flex flex-col gap-2.5 p-5">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold">Delivery history</span>
          {!subscription.enabled && subscription.disabledAt && (
            <span className="flex items-center gap-1.5 text-xs text-amber-text">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 3 2 20h20z" />
                <path d="M12 10v4M12 17h.01" />
              </svg>
              Auto-disabled after {CONSECUTIVE_FAILURES_TO_DISABLE} consecutive failed deliveries
            </span>
          )}
        </div>
        <div className="overflow-hidden rounded-input border border-border">
          <div className="grid h-7.5 items-center gap-1 bg-surface-subtle px-3 text-xs font-semibold text-text-muted" style={{ gridTemplateColumns: '1.3fr 1.1fr 56px 60px 74px 68px 92px' }}>
            <span>Event</span>
            <span>Delivery ID</span>
            <span>Status</span>
            <span>Attempt</span>
            <span>Next retry</span>
            <span>Duration</span>
            <span />
          </div>
          {(deliveries ?? []).length === 0 ? (
            <p className="p-3 text-sm text-text-muted">No deliveries yet.</p>
          ) : (
            (deliveries ?? []).slice(0, 8).map((d) => (
              <div key={d.id} className="grid h-10 items-center gap-1 border-t border-divider px-3 text-[13px]" style={{ gridTemplateColumns: '1.3fr 1.1fr 56px 60px 74px 68px 92px' }}>
                <span className="truncate font-mono text-xs">{d.eventType}</span>
                <span className="truncate font-mono text-xs text-text-muted">{d.id.slice(0, 8)}</span>
                <span>
                  <span
                    className="inline-flex h-5 items-center rounded-xs px-1.5 font-mono text-[11px] font-medium"
                    style={{ background: DELIVERY_STATUS_COLOR[d.status]?.bg, color: DELIVERY_STATUS_COLOR[d.status]?.fg }}
                  >
                    {d.responseStatus ?? d.status}
                  </span>
                </span>
                <span className="tabular-nums text-text-secondary">
                  {d.attempt}/{MAX_ATTEMPTS_PER_DELIVERY}
                </span>
                <span className="text-text-secondary">{d.nextAttemptAt ? new Date(d.nextAttemptAt).toLocaleTimeString() : '—'}</span>
                <span className="tabular-nums text-text-secondary">—</span>
                <span className="flex justify-end">
                  {d.status === 'failed' && (
                    <button
                      type="button"
                      onClick={() => redeliver.mutate(d.id)}
                      className="flex h-7 items-center rounded-chip border border-border-strong px-2.5 text-xs font-semibold"
                    >
                      Redeliver
                    </button>
                  )}
                </span>
              </div>
            ))
          )}
        </div>
        <span className="text-xs text-text-muted">Retries use exponential backoff · payloads kept 30 days</span>
      </div>
    </div>
  );
}

/** docs/architecture/08-v1-scope.md § "Webhooks & API" — combined per
 * design/orion-crm-design/rendered-html/AdminWebhooks.html, which shows one
 * screen for both; AdminShell's nav now points a single "API keys &
 * webhooks" entry here (`/admin/api-keys` redirects). */
export function WebhooksAdminPage() {
  const { data: keys, isLoading: keysLoading } = useApiKeysQuery();
  const revokeKey = useRevokeApiKey();
  const [showCreateKey, setShowCreateKey] = useState(false);
  const [keyToRevoke, setKeyToRevoke] = useState<ApiKeyResponse | null>(null);

  const { data: subscriptions, isLoading } = useWebhookSubscriptionsQuery();
  const createSubscription = useCreateWebhookSubscription();
  const deleteSubscription = useDeleteWebhookSubscription();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showAddEndpoint, setShowAddEndpoint] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);

  useEffect(() => {
    if (isDesignWebhooksPreview && subscriptions && selectedId === null) {
      const failing = subscriptions.find((s) => s.health.status === 'failing');
      if (failing) setSelectedId(failing.id);
    }
  }, [subscriptions, selectedId]);

  const selected = (subscriptions ?? []).find((s) => s.id === selectedId) ?? subscriptions?.[0];
  const failingCount = (subscriptions ?? []).filter((s) => s.health.status === 'failing').length;

  const breadcrumb = [{ label: 'Administration', to: '/admin' }, { label: 'API keys & webhooks' }];

  return (
    <>
      <PageHeader breadcrumb={breadcrumb} />
      <main className="flex flex-col gap-5 overflow-hidden px-8 py-7" style={{ height: 'calc(100vh - 60px)' }}>
        <div className="flex items-start justify-between gap-6">
          <div className="flex flex-col gap-1">
            <h1 className="text-[22px] font-[650] leading-7 tracking-[-0.01em]">API keys &amp; webhooks</h1>
            <p className="text-sm leading-5 text-text-muted">Let other systems read and write CRM data, and push events to your endpoints in real time.</p>
          </div>
          <Button size="md" onClick={() => setShowCreateKey(true)}>
            Create API key
          </Button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
          <section aria-label="API keys" className="shrink-0 overflow-hidden rounded-dialog border border-border bg-surface shadow-card">
            <div className="flex h-13 items-center justify-between px-5">
              <div className="flex items-baseline gap-2">
                <h2 className="text-[15px] font-semibold">API keys</h2>
                <span className="text-[13px] text-text-muted">{(keys ?? []).filter((k) => !k.revokedAt).length} active</span>
              </div>
              <span className="flex items-center gap-1.5 text-xs text-text-muted">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 11v5M12 8h.01" />
                </svg>
                Keys are shown once.
              </span>
            </div>
            {!keysLoading && (keys ?? []).length > 0 && (
              <>
                <div className="grid h-8 items-center px-5 text-xs font-semibold text-text-muted" style={{ gridTemplateColumns: '1.4fr 1.2fr 1.6fr 1fr 0.8fr 0.9fr 110px', background: 'var(--color-surface-subtle)', borderTop: '1px solid var(--color-border)', borderBottom: '1px solid var(--color-border)' }}>
                  <span>Name</span>
                  <span>Key</span>
                  <span>Scopes</span>
                  <span>Created by</span>
                  <span>Last used</span>
                  <span>Expires</span>
                  <span className="text-right">Status</span>
                </div>
                {(keys ?? []).map((k) => {
                  const revoked = Boolean(k.revokedAt);
                  return (
                    <div
                      key={k.id}
                      className={`grid h-11 items-center border-b border-divider px-5 text-sm last:border-b-0 ${revoked ? 'opacity-55' : ''}`}
                      style={{ gridTemplateColumns: '1.4fr 1.2fr 1.6fr 1fr 0.8fr 0.9fr 110px' }}
                    >
                      <span className="flex min-w-0 items-center gap-2 font-semibold">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0 text-text-secondary">
                          <circle cx="8" cy="15" r="4" />
                          <path d="m11 12 9-9M17 6l3 3" />
                        </svg>
                        <span className={`truncate ${revoked ? 'line-through decoration-text-muted' : ''}`}>{k.name}</span>
                      </span>
                      <span className="truncate font-mono text-xs text-text-secondary">{k.prefix}…</span>
                      <span className="flex min-w-0 gap-1 overflow-hidden">
                        {k.scopes.slice(0, 2).map((s) => (
                          <span key={s} className="flex h-5.5 shrink-0 items-center rounded-xs border border-border px-1.75 font-mono text-[11px] text-text-secondary">
                            {s}
                          </span>
                        ))}
                        {k.scopes.length > 2 && (
                          <span
                            title={k.scopes.slice(2).join(', ')}
                            className="flex h-5.5 shrink-0 items-center rounded-xs border border-border bg-surface-subtle px-1.75 font-mono text-[11px] text-text-muted"
                          >
                            +{k.scopes.length - 2}
                          </span>
                        )}
                      </span>
                      <span className="flex min-w-0 items-center gap-2 text-[13px] text-text-secondary">
                        {k.createdByName && (
                          <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-avatar-indigo text-[9px] font-bold text-white">{initials(k.createdByName)}</span>
                        )}
                        <span className="truncate">{k.createdByName ?? '—'}</span>
                      </span>
                      <span className="truncate text-[13px] text-text-secondary">{k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleDateString() : 'Never'}</span>
                      <span className="truncate text-[13px] text-text-secondary">{k.expiresAt ? new Date(k.expiresAt).toLocaleDateString() : 'Never'}</span>
                      <span className="flex justify-end">
                        {revoked ? (
                          <span className="flex h-6 items-center gap-1.5 rounded-full bg-surface-subtle px-2.5 text-xs font-semibold text-text-muted">
                            <span className="size-1.5 rounded-full bg-text-faint" />
                            Revoked
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setKeyToRevoke(k)}
                            className="flex h-7 items-center gap-1.5 rounded-chip border border-border-strong px-2.5 text-xs font-semibold text-danger transition-colors hover:border-danger hover:bg-danger-soft"
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <circle cx="12" cy="12" r="9" />
                              <path d="m9 9 6 6M15 9l-6 6" />
                            </svg>
                            Revoke
                          </button>
                        )}
                      </span>
                    </div>
                  );
                })}
              </>
            )}
          </section>

          <section aria-label="Webhooks" className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-dialog border border-border bg-surface shadow-card">
            <div className="flex h-13 shrink-0 items-center justify-between px-5">
              <div className="flex items-baseline gap-2">
                <h2 className="text-[15px] font-semibold">Webhooks</h2>
                <span className="text-[13px] text-text-muted">
                  {(subscriptions ?? []).length} endpoints{failingCount > 0 ? ` · ${failingCount} failing` : ''}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setShowAddEndpoint((s) => !s)}
                className="flex h-8 items-center gap-1.5 rounded-chip border border-border-strong px-2.5 text-[13px] font-semibold"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" aria-hidden="true">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                Add endpoint
              </button>
            </div>

            {showAddEndpoint && (
              <div className="flex shrink-0 gap-2 border-t border-divider bg-surface-subtle px-5 py-3">
                <Input label="Endpoint URL" hideLabel value={newUrl} onChange={(e) => setNewUrl(e.target.value)} placeholder="https://example.com/webhooks/orion" className="flex-1" />
                <Button
                  size="sm"
                  loading={createSubscription.isPending}
                  onClick={async () => {
                    if (!newUrl.trim()) return;
                    const result = await createSubscription.mutateAsync({ url: newUrl.trim(), eventTypes: [], enabled: true });
                    setRevealedSecret(result.secret);
                    setSelectedId(result.id);
                    setNewUrl('');
                    setShowAddEndpoint(false);
                  }}
                >
                  Add
                </Button>
              </div>
            )}
            {revealedSecret && (
              <div className="mx-5 mt-3 shrink-0 rounded-control border border-amber-text/30 bg-amber-soft p-3 text-xs">
                <p className="mb-1 font-semibold text-amber-text">Copy this signing secret now — it won&rsquo;t be shown again</p>
                <code className="block break-all">{revealedSecret}</code>
                <button type="button" onClick={() => setRevealedSecret(null)} className="mt-1.5 font-semibold text-primary">
                  Done
                </button>
              </div>
            )}

            {!isLoading && (subscriptions ?? []).length > 0 && (
              <div role="listbox" aria-label="Webhook endpoints" className="shrink-0 border-t border-divider">
                {(subscriptions ?? []).map((s) => {
                  const isSelected = s.id === selected?.id;
                  return (
                    <div
                      key={s.id}
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => setSelectedId(s.id)}
                      className={`grid h-12 cursor-pointer items-center gap-2 border-b border-divider px-5 text-sm ${isSelected ? 'bg-primary-soft shadow-[inset_0_0_0_1.5px_var(--color-primary)]' : ''}`}
                      style={{ gridTemplateColumns: '1.6fr 1.4fr 200px 24px' }}
                    >
                      <span className="flex min-w-0 items-center gap-2.5">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={s.health.status === 'failing' ? 'var(--color-danger)' : 'currentColor'} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0 text-text-secondary">
                          <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" />
                          <path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" />
                        </svg>
                        <span className="truncate font-mono text-xs font-medium">{s.url}</span>
                      </span>
                      <span className="flex gap-1">
                        {s.eventTypes.length === 0 ? (
                          <span className="text-xs text-text-muted">All events</span>
                        ) : (
                          s.eventTypes.slice(0, 2).map((e) => (
                            <span key={e} className="flex h-5.5 items-center rounded-xs border border-border bg-surface px-1.75 font-mono text-[11px] text-text-secondary">
                              {e}
                            </span>
                          ))
                        )}
                      </span>
                      {s.health.status === 'failing' ? (
                        <span className="flex flex-col items-start gap-px">
                          <span className="flex h-5 items-center gap-1.5 rounded-full bg-danger-soft px-2.5 text-xs font-semibold text-danger">
                            <span className="size-1.5 rounded-full bg-danger" />
                            Failing
                          </span>
                          {s.health.recentFailureSummary && <span className="text-[11px] font-medium text-amber-text">{s.health.recentFailureSummary}</span>}
                        </span>
                      ) : s.health.status === 'healthy' ? (
                        <span className="flex items-center gap-2">
                          <span className="flex h-6 items-center gap-1.5 rounded-full bg-success-soft px-2.5 text-xs font-semibold text-success">
                            <span className="size-1.5 rounded-full bg-success" />
                            Healthy
                          </span>
                          <span className="text-xs text-text-muted">{s.health.successRatePct}% · 7 days</span>
                        </span>
                      ) : (
                        <span className="text-xs text-text-muted">No deliveries yet</span>
                      )}
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="text-text-faint">
                        <path d="m9 6 6 6-6 6" />
                      </svg>
                    </div>
                  );
                })}
              </div>
            )}

            {selected && (
              <div className="min-h-0 flex-1 overflow-y-auto">
                <WebhookDetail subscription={selected} />
                <div className="flex justify-end px-5 py-2">
                  <button type="button" onClick={() => deleteSubscription.mutate(selected.id)} className="text-xs font-semibold text-danger">
                    Delete endpoint
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>
      </main>
      {showCreateKey && <CreateApiKeyDialog onClose={() => setShowCreateKey(false)} />}
      {keyToRevoke && (
        <ConfirmDialog
          open
          title={`Revoke "${keyToRevoke.name}"?`}
          description="Any integration using this key will immediately lose access. This can't be undone."
          confirmLabel="Revoke key"
          variant="danger"
          loading={revokeKey.isPending}
          onConfirm={async () => {
            await revokeKey.mutateAsync(keyToRevoke.id);
            setKeyToRevoke(null);
          }}
          onClose={() => setKeyToRevoke(null)}
        />
      )}
    </>
  );
}
